import type { UnvaluedReasonContext } from './definitions';
import { UnvaluedReason } from './unvalued-reason';
import { reasonFixParts } from './utils';

/** The row fields the cell reads: the landing verdict, the reasons list, and the served hrefs. */
export interface UnvaluedReasonRow extends UnvaluedReasonContext {
  /** The row's SERVED well href — never one built from the slot id. */
  slotIdHref?: string | null;
  /** The row's served DPS area href (present only when a pace area matched the slot). */
  dpsAreaHref?: string | null;
}

interface UnvaluedReasonCellProps {
  /** The row's joined unvalued reason. */
  reason: string | null | undefined;
  row: UnvaluedReasonRow;
  /** The package whose wizard an exclusion links to; null → an exclusion is unlinked. */
  packageId: number | null;
  /** What to render when there is no reason. */
  fallback?: string;
}

/**
 * An Unvalued Reason table cell, for every table that shows one (the eval3 Slot and Slot
 * Allocation grids, the NRI table): the reason with its definition tooltip (INS-12685), each
 * fixable reason linked to where it gets fixed (INS-12687).
 *
 * `dpsAreaHref` is served only when a pace area matched the slot, which an unscheduled slot
 * rarely has, so "Not scheduled" links in a table only then; the slot tearsheet, which always
 * has the run's Development Pace Scenario, links it for every unscheduled slot.
 *
 * @param {UnvaluedReasonCellProps} props - The reason, its row, the package, and the fallback.
 * @return {JSX.Element} The reason cell.
 */
export function UnvaluedReasonCell({
  reason,
  row,
  packageId,
  fallback,
}: UnvaluedReasonCellProps) {
  const parts = reasonFixParts(reason, {
    packageId,
    wellHref: row.slotIdHref,
    dpsHref: row.dpsAreaHref,
    landedWithoutFormation: row.landedWithoutFormation,
    unvaluedReasons: row.unvaluedReasons,
  });
  return (
    <UnvaluedReason
      reason={reason}
      fallback={fallback}
      landedWithoutFormation={row.landedWithoutFormation}
      landingInterval={row.landingInterval}
      unvaluedReasons={row.unvaluedReasons}
      parts={parts}
    />
  );
}
